import {
  Alert,
  Box,
  Button,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { useState } from "react";
import {
  BroadcastFilter,
  ContentData,
  CustomRecipient,
  RecipientSources,
  defaultRecipientSources,
  isValidRecipientEmail,
} from "./broadcastTypes";
import TypeSelector from "./steps/TypeSelector";
import EventInvitationContent from "./steps/EventInvitationContent";
import CustomEmailContent from "./steps/CustomEmailContent";
import RecipientFilter from "./steps/RecipientFilter";
import ReviewAndSend from "./steps/ReviewAndSend";
import { useViewport } from "../../../utils/useViewport";
import { visuallyHidden } from "../../../utils/visuallyHidden";

const STEPS = ["Choose Type", "Compose", "Target Recipients", "Review & Send"];

const defaultContentData: ContentData = {
  subject: "",
  htmlContent: "",
};

const defaultFilter: BroadcastFilter = {};

interface BroadcastBuilderProps {
  onSendSuccess: () => void;
}

const BroadcastBuilder = ({ onSendSuccess }: BroadcastBuilderProps) => {
  const { isPhone, isCompact } = useViewport();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedType, setSelectedType] = useState<string>("");
  const [contentData, setContentData] = useState<ContentData>(defaultContentData);
  const [filter, setFilter] = useState<BroadcastFilter>(defaultFilter);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [recipientSources, setRecipientSources] = useState<RecipientSources>(
    defaultRecipientSources,
  );
  const [customRecipients, setCustomRecipients] = useState<CustomRecipient[]>([]);

  const validCustomRecipients = customRecipients.filter((r) =>
    isValidRecipientEmail(r.email),
  );

  const canAdvanceFromStep = (step: number): boolean => {
    if (step === 0) return selectedType !== "";
    if (step === 1) return contentData.subject.trim() !== "" && contentData.htmlContent.trim() !== "";
    if (step === 2) {
      // At least one source, and a custom source must actually carry addresses.
      if (!recipientSources.firms && !recipientSources.custom) return false;
      if (recipientSources.custom && validCustomRecipients.length === 0) return false;
      return recipientCount !== null && recipientCount > 0;
    }
    return false;
  };

  const handleTypeSelect = (typeId: string) => {
    setSelectedType(typeId);
    // Reset content when type changes
    setContentData(defaultContentData);
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <TypeSelector
            selectedType={selectedType}
            onSelect={(typeId) => {
              handleTypeSelect(typeId);
            }}
          />
        );
      case 1:
        if (selectedType === "event_invitation") {
          return (
            <EventInvitationContent
              contentData={contentData}
              setContentData={setContentData}
              onNext={handleNext}
            />
          );
        }
        return (
          <CustomEmailContent
            contentData={contentData}
            setContentData={setContentData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <RecipientFilter
            filter={filter}
            setFilter={setFilter}
            recipientCount={recipientCount}
            setRecipientCount={setRecipientCount}
            recipientSources={recipientSources}
            setRecipientSources={setRecipientSources}
            customRecipients={customRecipients}
            setCustomRecipients={setCustomRecipients}
          />
        );
      case 3:
        return (
          <ReviewAndSend
            broadcastType={selectedType}
            contentData={contentData}
            filter={filter}
            recipientCount={recipientCount}
            recipientSources={recipientSources}
            customRecipients={validCustomRecipients}
            onSendSuccess={onSendSuccess}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      {/* Below 900 px the labels sit under the step icons; phones show only
          the active step's label (the others stay readable to screen
          readers). */}
      <Stepper
        activeStep={activeStep}
        alternativeLabel={isCompact}
        sx={{ mb: 4 }}
      >
        {STEPS.map((label, index) => (
          <Step key={label}>
            <StepLabel
              sx={{
                "& .MuiStepLabel-label.Mui-active": { color: "#9d2362" },
                "& .MuiStepIcon-root.Mui-active": { color: "#9d2362" },
                "& .MuiStepIcon-root.Mui-completed": { color: "#9d2362" },
                ...(isPhone &&
                  index !== activeStep && {
                    "& .MuiStepLabel-labelContainer": visuallyHidden,
                  }),
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 320 }}>{renderStepContent()}</Box>

      {activeStep === 2 &&
        recipientCount === null &&
        (recipientSources.firms || recipientSources.custom) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Click <strong>Preview Recipients</strong> above before proceeding to the next step.
          </Alert>
        )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 4,
          pt: 2,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{ color: "#9d2362" }}
          >
            Back
          </Button>
          {activeStep === 2 && (
            <Button
              startIcon={<FilterAltOffIcon />}
              onClick={() => {
                setFilter(defaultFilter);
                setRecipientCount(null);
              }}
              sx={{ color: "#666" }}
            >
              Reset Filter
            </Button>
          )}
        </Box>

        {activeStep < STEPS.length - 1 && (
          <Button
            endIcon={<ArrowForwardIcon />}
            variant="contained"
            onClick={handleNext}
            disabled={!canAdvanceFromStep(activeStep)}
            sx={{
              backgroundColor: "#9d2362",
              "&:hover": { backgroundColor: "#7a1a4d" },
              "&.Mui-disabled": { backgroundColor: "#e0e0e0" },
            }}
          >
            Next
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default BroadcastBuilder;
